import useApi from "../services/ApiService";
import {User, UserRole} from "../models/User";
import { AxiosResponse } from "axios";
import {Paging} from "../models/Paging";

export interface GetUsersQueryOptions {
  roles?: UserRole[];
  searchPhrase?: string;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  fetchAll?: boolean;
  onlyWithOrganizations?: boolean;
  showDeactivated?: boolean;
}

export class UsersRepository {
  private api = useApi();

  async getUsers(
    options: GetUsersQueryOptions = {}
  ): Promise<{ data: User[]; paging: Paging | null }> {
    const {
      roles = null,
      searchPhrase = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      fetchAll = null,
      onlyWithOrganizations = false,
      showDeactivated = false,
    } = options;
    try {
      const params = new URLSearchParams();
      if (roles && roles.length > 0)
        roles.forEach(role => params.append("Roles", role.toString()));
      if (searchPhrase)
        params.append("SearchPhrase", searchPhrase);
      if (pageNumber) 
        params.append("PageNumber", pageNumber.toString());
      if (pageSize)
        params.append("PageSize", pageSize.toString());
      if (orderBy) 
        params.append("OrderBy", orderBy);
      if (descending)
        params.append("Descending", descending.toString());
      if (fetchAll)
        params.append("FetchAll", fetchAll.toString());
      if (onlyWithOrganizations)
        params.append("OnlyWithOrganizations", onlyWithOrganizations.toString());
      if (showDeactivated)
        params.append("ShowDeactivated", showDeactivated.toString());

      const response = await this.api.get("/api/Users", { params });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");

      return { data, paging };
    } catch (error) {
      throw new Error("Failed to fetch users");
    }
  }

  async createUser(
    username: string,
    email: string,
    password: string,
    role: UserRole
  ): Promise<User | null> {
    try {
      const response: AxiosResponse<User> = await this.api.post("/api/Users/", {
        username: username,
        email: email,
        password: password,
        role: role,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateUser(id: string, updatedUser: User): Promise<User> {
    try {
      const response: AxiosResponse<User> = await this.api.patch(
        `/api/Users/${id}`,
        updatedUser
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      return await this.api.delete(`/api/Users/${userId}`);
    } catch (error) {
      throw new Error("Failed to delete user");
    }
  }

  async reactivateUser(userId: string): Promise<void> {
    try {
      await this.api.post(`/api/Users/${userId}/reactivate`);
    } catch (error) {
      throw new Error("Failed to reactivate user");
    }
  }

  async resetPassword(userId: string, newPassword: string): Promise<void> {
    try {
      await this.api.post(`/api/Users/${userId}/reset-password`, { newPassword });
    } catch (error) {
      throw error;
    }
  }

  async changeMyPassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await this.api.post("/api/Users/change-password", { currentPassword, newPassword });
    } catch (error) {
      throw error;
    }
  }
}

export default UsersRepository;
